/**
 * Admin Dashboard
 * System overview with real-time metrics
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../../config/api';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { QueueMonitor } from '../../components/QueueMonitor';
import {
  ChartBarIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface DashboardStats {
  software: {
    total_software: number;
    total_versions: number;
    latest_versions: number;
  };
  cves: {
    total_cves: number;
    critical: number;
    high: number;
    new_last_7_days: number;
  };
  jobs: {
    total_jobs_24h: number;
    completed_24h: number;
    failed_24h: number;
  };
  recent_activity: Array<{
    id: number;
    job_type: string;
    status: string;
    software_name: string | null;
    completed_at: string;
    result: string | null;
  }>;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(getApiUrl('api/v1/admin/dashboard-stats'));
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'failed':
        return 'text-red-400';
      case 'running':
        return 'text-blue-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-100">Admin Dashboard</h1>
          <p className="text-gray-400 mt-1">System overview and real-time metrics</p>
        </div>
        <button
          onClick={loadStats}
          className="flex items-center gap-2 px-4 py-2 bg-cyber-blue-500/10 text-cyber-blue-400 border border-cyber-blue-500/30 rounded hover:bg-cyber-blue-500/20 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Main Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Software */}
          <div className="bg-cyber-blue-500/10 border border-cyber-blue-500/30 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <ShieldCheckIcon className="w-10 h-10 text-cyber-blue-400" />
            </div>
            <div className="text-3xl font-bold text-cyber-blue-400 mb-1">
              {stats.software.total_software}
            </div>
            <div className="text-sm text-gray-400">Total Software Products</div>
          </div>

          {/* Total Versions */}
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <ChartBarIcon className="w-10 h-10 text-green-400" />
            </div>
            <div className="text-3xl font-bold text-green-400 mb-1">
              {stats.software.total_versions}
            </div>
            <div className="text-sm text-gray-400">Versions Tracked</div>
            <div className="text-xs text-green-300 mt-1">
              {stats.software.latest_versions} latest
            </div>
          </div>

          {/* Total CVEs */}
          <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <ExclamationTriangleIcon className="w-10 h-10 text-orange-400" />
            </div>
            <div className="text-3xl font-bold text-orange-400 mb-1">
              {stats.cves.total_cves}
            </div>
            <div className="text-sm text-gray-400">Total CVEs</div>
            <div className="text-xs text-red-300 mt-1">
              {stats.cves.critical} critical, {stats.cves.high} high
            </div>
          </div>

          {/* Jobs Today */}
          <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <BriefcaseIcon className="w-10 h-10 text-purple-400" />
            </div>
            <div className="text-3xl font-bold text-purple-400 mb-1">
              {stats.jobs.total_jobs_24h}
            </div>
            <div className="text-sm text-gray-400">Jobs (24h)</div>
            <div className="text-xs text-purple-300 mt-1">
              {stats.jobs.completed_24h} completed, {stats.jobs.failed_24h} failed
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity & Quick Links Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-gray-800/30 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-100 mb-4">Recent Activity</h2>
          {stats && stats.recent_activity.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_activity.map((activity) => (
                <Link
                  key={activity.id}
                  to={`/admin/jobs/${activity.id}`}
                  className="block bg-gray-900/30 border border-gray-700/50 rounded p-3 hover:bg-gray-900/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {activity.status === 'completed' ? (
                          <CheckCircleIcon className="w-4 h-4 text-green-400" />
                        ) : activity.status === 'failed' ? (
                          <XCircleIcon className="w-4 h-4 text-red-400" />
                        ) : (
                          <ArrowPathIcon className="w-4 h-4 text-blue-400" />
                        )}
                        <span className={`text-sm font-medium ${getJobStatusColor(activity.status)}`}>
                          Job #{activity.id}
                        </span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-sm text-gray-400 capitalize">{activity.job_type}</span>
                      </div>
                      {activity.software_name && (
                        <p className="text-sm text-gray-300">{activity.software_name}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                      {new Date(activity.completed_at).toLocaleTimeString()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No recent activity</p>
          )}
        </div>

        {/* Quick Links */}
        <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-100 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link
              to="/admin/jobs"
              className="block p-3 bg-cyber-blue-500/10 border border-cyber-blue-500/30 rounded hover:bg-cyber-blue-500/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <BriefcaseIcon className="w-5 h-5 text-cyber-blue-400" />
                <span className="text-sm font-medium text-cyber-blue-400">View All Jobs</span>
              </div>
            </Link>
            <Link
              to="/admin/settings"
              className="block p-3 bg-purple-500/10 border border-purple-500/30 rounded hover:bg-purple-500/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <ChartBarIcon className="w-5 h-5 text-purple-400" />
                <span className="text-sm font-medium text-purple-400">AI Indexing Stats</span>
              </div>
            </Link>
            <Link
              to="/catalog"
              className="block p-3 bg-green-500/10 border border-green-500/30 rounded hover:bg-green-500/20 transition-colors"
            >
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="w-5 h-5 text-green-400" />
                <span className="text-sm font-medium text-green-400">Software Catalog</span>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* CVE Statistics */}
      {stats && (
        <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-100 mb-4">CVE Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-900/30 border border-gray-700/50 rounded p-4">
              <div className="text-sm text-gray-400 mb-1">Total CVEs</div>
              <div className="text-2xl font-bold text-gray-100">{stats.cves.total_cves}</div>
            </div>
            <div className="bg-red-900/20 border border-red-500/30 rounded p-4">
              <div className="text-sm text-red-400 mb-1">Critical</div>
              <div className="text-2xl font-bold text-red-400">{stats.cves.critical}</div>
            </div>
            <div className="bg-orange-900/20 border border-orange-500/30 rounded p-4">
              <div className="text-sm text-orange-400 mb-1">High</div>
              <div className="text-2xl font-bold text-orange-400">{stats.cves.high}</div>
            </div>
            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-4">
              <div className="text-sm text-yellow-400 mb-1">New (7 days)</div>
              <div className="text-2xl font-bold text-yellow-400">{stats.cves.new_last_7_days}</div>
            </div>
          </div>
        </div>
      )}

      {/* Queue Monitor */}
      <QueueMonitor />
    </div>
  );
}
